import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import { clusterContextSelector } from '@selectors';
import { useSelector } from '@hooks';
import { as, getFormattedMemory, getGpuFromResourcePreset } from '@utils';

import { JobDetailsItem } from '@components/Job';
import { Button, Icon, Modal, Render } from '@components/Shared';
import { GpuModels } from '@components/Ui';

type Props = {
  value: string;
};

export const ParamReferenceResourcePreset: React.FC<Props> = ({ value }) => {
  const cluster = useSelector(clusterContextSelector);

  const { resourcePresets = [] } = as.c(cluster);

  const resourcePreset = resourcePresets.find(({ name }) => name === value);

  if (!resourcePreset) {
    return <span className="text-lime-800">{value}</span>;
  }

  const {
    name,
    cpu,
    memory,
    resourcePoolNames,
    schedulerEnabled,
    creditsPerHour,
    isPreemptible,
  } = resourcePreset;
  const gpuModels = getGpuFromResourcePreset(resourcePreset);

  const modalContent = (
    <Modal.Content
      title={`Resource Preset ${value}`}
      className="w-[640px] pb-10"
    >
      <div className="flex flex-col gap-4">
        <JobDetailsItem title="Name" text={name} className="gap-1" />
        <JobDetailsItem title="CPU" text={cpu} className="gap-1" />
        <JobDetailsItem
          title="Memory"
          text={getFormattedMemory(memory)}
          className="gap-1"
        />
        <JobDetailsItem title="GPU Devices" className="gap-1">
          <Render if={gpuModels.length > 0}>
            <GpuModels models={gpuModels} />
          </Render>
          <Render if={gpuModels.length === 0}>-</Render>
        </JobDetailsItem>
        <JobDetailsItem title="Resource Pools" className="gap-1">
          <Render if={resourcePoolNames}>
            <p>{resourcePoolNames?.join(', ')}</p>
          </Render>
          <Render if={!resourcePoolNames}>-</Render>
        </JobDetailsItem>
        <JobDetailsItem
          title="Round-Robin"
          text={schedulerEnabled ? 'Yes' : 'No'}
          className="gap-1"
        />
        <JobDetailsItem
          title="Preemptability"
          text={isPreemptible ? 'Yes' : 'No'}
          className="gap-1"
        />
        <JobDetailsItem
          title="Credits / Hour"
          text={creditsPerHour}
          className="gap-1"
        />
      </div>
    </Modal.Content>
  );

  return (
    <Modal content={modalContent}>
      <Button
        theme
        variant="ghost"
        className="flex items-center gap-1.5 text-primary"
      >
        {value} <Icon icon={faCircleInfo} className="text-[18px]" />
      </Button>
    </Modal>
  );
};
